import { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page } from 'react-pdf';
import classNames from 'classnames';
import styles from './PdfPreview.module.scss';
import 'pdfjs-dist/build/pdf.worker.mjs';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

const INITIAL_SCALE = 1.0;
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.0;
const SCALE_STEP = 0.25;

const PdfPreview = () => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const documentRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const fileUrl = queryParams.get('file');

    if (fileUrl) {
      setPdfUrl(fileUrl);
    }
  }, [location]);

  // Scroll-based page tracking
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        if (!documentRef.current) return;

        let mostVisiblePage = 1;
        let maxVisibility = 0;

        pageRefs.current.forEach((pageRef, index) => {
          if (!pageRef) return;

          const rect = pageRef.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
          const pageHeight = rect.height;
          const visibilityPercentage = (visibleHeight / pageHeight) * 100;

          if (visibilityPercentage > maxVisibility) {
            maxVisibility = visibilityPercentage;
            mostVisiblePage = index + 1;
          }
        });

        setPageNumber(mostVisiblePage);
      }, 100); // Debounced for efficiency
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [numPages]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setIsError(false);
    pageRefs.current = new Array(numPages).fill(null);
  };

  const onDocumentLoadError = () => {
    setLoading(false);
    setIsError(true);
  };

  // Zoom Controls
  const zoomIn = useCallback(() => setScale((prev) => Math.min(prev + SCALE_STEP, MAX_SCALE)), []);
  const zoomOut = useCallback(() => setScale((prev) => Math.max(prev - SCALE_STEP, MIN_SCALE)), []);
  const resetZoom = useCallback(() => setScale(INITIAL_SCALE), []);

  return (
    <div className={styles['pdf-preview-wrapper']}>
      {loading && <div className={styles['pdf-render-result']}>Loading...</div>}
      {isError && <div className={styles['pdf-render-result']}>Failed to load pdf!!!</div>}
      <div ref={documentRef} className={styles['preview-body']}>
        <Document
          file={pdfUrl}
          loading=""
          error=""
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
        >
          {Array.from(new Array(numPages), (_, index) => (
            <div
              key={index}
              ref={(el) => {
                if (pageRefs.current) pageRefs.current[index] = el;
              }}
            >
              <Page pageNumber={index + 1} className="!bg-black" scale={scale} />
            </div>
          ))}
        </Document>
      </div>
      {pdfUrl && !loading && !isError && (
        <div className={styles['pdf-preview-options']}>
          <div className={styles['pdf-paginator']}>
            <p>Page</p>
            <small>
              {pageNumber} / {numPages}
            </small>
          </div>
          <div className={styles['pdf-zoomer']}>
            <p className={styles['zoomOut']} onClick={zoomOut}>
              <span>-</span>
            </p>
            <p className={styles['zoomIn']} onClick={zoomIn}>
              <span>+</span>
            </p>
            <p
              className={classNames(styles['zoomReset'], {
                [styles['zoomResetDisabled']]: scale === INITIAL_SCALE,
              })}
              onClick={resetZoom}
            >
              <span>Reset</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfPreview;
